import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw } from "lucide-react";
import { useSignedPhotoUrl, getSignedPhotoUrl, invalidatePhotoUrl } from "@/lib/signedMedia";

// Best-effort status probe for troubleshooting logs — never logs the URL
// itself (it carries a short-lived signature).
async function probeStatus(url) {
  try {
    const r = await fetch(url, { cache: "no-store" });
    return r.status;
  } catch {
    return 0; // network/CORS-level failure
  }
}

export default function PhotoImg({ photoId, token, alt = "", className = "", testid, mediaType, thumb = false, preUrl = "", onBroken }) {
  // Wave 2: grids pass a pre-signed thumb URL from /photos/page — no sign
  // round-trip. On error, refetch() falls back to a fresh signed URL.
  const signed = useSignedPhotoUrl(preUrl ? null : photoId, thumb, token);
  const [override, setOverride] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  const [attempt, setAttempt] = useState(0); // 0 = initial load, 1 = auto-retry used
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  useEffect(() => { setOverride(""); setLoaded(false); setBroken(false); setAttempt(0); }, [photoId, thumb, token]);

  const src = override || preUrl || signed;
  const failed = !override && signed === null; // sign resolved but produced no URL

  const refetch = async () => {
    invalidatePhotoUrl(photoId);
    let fresh = "";
    try { fresh = await getSignedPhotoUrl(photoId, thumb, token); } catch { /* fall through */ }
    if (!alive.current) return;
    if (fresh) { setOverride(fresh); setLoaded(false); setBroken(false); }
    else { setBroken(true); onBroken?.(); }
  };

  const handleError = async () => {
    const status = src ? await probeStatus(src) : 0;
    console.warn("[photo] load failed", { photoId, status, thumb, retried: attempt > 0 });
    if (!alive.current) return;
    if (attempt === 0) {
      setAttempt(1);
      setTimeout(refetch, 800); // one automatic retry with a short backoff
    } else {
      setBroken(true);
      onBroken?.();
    }
  };

  const manualRetry = async (e) => {
    e.stopPropagation();
    setAttempt(1);
    await refetch();
  };

  if (broken || failed) {
    // Parent-provided fallback (e.g. project covers cycle to the next photo)
    // takes precedence over the inline Retry placeholder.
    if (onBroken) return null;
    return (
      <button type="button" onClick={manualRetry}
        className={`flex flex-col items-center justify-center gap-1 bg-slate-100 text-slate-500 ${className}`}
        data-testid={testid ? `${testid}-retry` : undefined} aria-label="Retry loading photo">
        <Camera className="h-8 w-8" />
        <span className="inline-flex items-center gap-1 text-[10px] font-bold"><RefreshCw className="h-3 w-3" /> Retry</span>
      </button>
    );
  }
  if (!src) {
    return <div className={`bg-slate-100 animate-pulse ${className}`} data-testid={testid ? `${testid}-loading` : undefined} />;
  }
  if (mediaType === "video") {
    return <video src={src} className={className} data-testid={testid} muted playsInline preload="metadata" />;
  }
  return (
    <img
      key={src}
      src={src}
      alt={alt}
      className={`${className}${loaded ? "" : " bg-slate-100 animate-pulse"}`}
      data-testid={testid}
      loading="lazy"
      onLoad={() => setLoaded(true)}
      onError={handleError}
    />
  );
}
